/**
 * A small "(i)" affordance: icon button → short plain-English popover.
 *
 * Written by hand rather than pulled in as a tooltip library because the SPA
 * has no popover/tooltip primitive today, and a compliance form is the wrong
 * place to introduce a second one later. The behaviour it needs is small and
 * fully specified:
 *
 *  - It is a `<button>`, not a hover target. Hover-only help is invisible on
 *    the touch devices the mobile mandate names, and a CPA filling in an
 *    independence attestation on an iPad is exactly the case that matters.
 *  - `aria-label` is the field name ("Help — Peer review expires"), so a screen
 *    reader announces *which* field the help belongs to, not just "more info".
 *  - Escape closes and returns focus to the trigger; a click anywhere outside
 *    closes. `aria-expanded` + `role="dialog"` keep the two in step.
 *
 * The panel is absolutely positioned inside a `relative` inline wrapper, so it
 * escapes the label's text flow without needing a portal.
 *
 * Below `lg` it is not a popover at all but a card pinned to the bottom-right
 * of the viewport. Anchoring `right-0` to the dot is fine while the dot sits
 * near the right edge; for a dot that sits mid-line — "Declared interests" is
 * one — a 320px panel hanging off it starts at a negative x, and the first few
 * characters of every line are cut off (at 375) or hidden behind the fixed
 * sidebar rail (at 768). Neither registers as horizontal overflow — there is
 * nothing to scroll *to* on the left — so it passes the responsive harness and
 * fails the reader, which is the worst combination: help text that is present,
 * unreadable and unflagged. Both were caught by looking at the screenshots the
 * harness writes, which is why the DoD asks for them. Pinning to the viewport
 * removes the dependence on where in the line the dot happens to fall.
 *
 * Bundle 2 item 5: the anchored popover that returns at `lg` had the same bug,
 * one breakpoint up. `right-0` puts the panel's *right* edge on the dot, so it
 * grows leftward — from the "Intended users" dot, which sits mid-line in the
 * left column of a two-column grid, 320px of leftward growth starts off the
 * card entirely. From a dot in the right-hand column it lands on top of the
 * neighbouring field instead: inside the viewport, so nothing flags it, and
 * still unreadable. Widening the column does not fix it; the panel's placement
 * simply cannot be inferred from CSS alone, because it depends on where in its
 * line the dot fell.
 *
 * So the one thing CSS cannot know is measured: `panelShift` reads the dot's
 * viewport x on open and returns how far left the panel must slide to fit. The
 * panel left-aligns with the dot by default and only ever moves toward the
 * left, never past the viewport edge. It stays `absolute` — anchored to the
 * dot, so it scrolls with the page and needs no scroll listener; vertical
 * scrolling cannot change a horizontal fit. Only a resize can, and that is the
 * one thing re-measured. An open popover overlapping the content beside it is
 * a popover behaving normally; being clipped by the window is not.
 */
import * as React from 'react'
import { Info } from 'lucide-react'

import { cn } from '@/lib/utils'

/** `lg:w-80` in px, and the gap kept between the panel and the window edge. */
const PANEL_WIDTH = 320
const VIEWPORT_MARGIN = 12

/**
 * How far to slide the `lg` panel left of the dot so it fits on screen.
 *
 * Always `<= 0`. The preferred position is flush with the dot (`0`); the only
 * adjustment ever made is leftward, and never far enough to push the panel off
 * the left edge in the course of rescuing it from the right one.
 *
 * Exported for its own test: the placement is arithmetic, and arithmetic is
 * worth pinning down somewhere other than a screenshot.
 */
export function panelShift(dotLeft: number, viewportWidth: number): number {
  const rightmostFit = viewportWidth - VIEWPORT_MARGIN - PANEL_WIDTH
  return Math.min(0, Math.max(rightmostFit, VIEWPORT_MARGIN) - dotLeft)
}

export interface InfoDotProps {
  /** The field this help belongs to. Becomes the accessible name. */
  label: string
  /** Popover body. Short paragraphs; keep it plain-English. */
  children: React.ReactNode
  className?: string
  /** Test hook — the caller usually passes `info-<field-id>`. */
  'data-testid'?: string
}

export function InfoDot({
  label,
  children,
  className,
  'data-testid': testId,
}: InfoDotProps) {
  const [open, setOpen] = React.useState(false)
  const [shift, setShift] = React.useState(0)
  const wrapRef = React.useRef<HTMLSpanElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)

  // Layout effect, not effect: the panel must not paint at the wrong x and
  // then jump. `clientWidth` rather than `innerWidth` — the scrollbar is not
  // space the panel can use.
  React.useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const rect = buttonRef.current?.getBoundingClientRect()
      if (rect) setShift(panelShift(rect.left, document.documentElement.clientWidth))
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [open])

  React.useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    const onPointer = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPointer)
    }
  }, [open])

  return (
    <span ref={wrapRef} className={cn('relative inline-flex align-middle', className)}>
      <button
        ref={buttonRef}
        type="button"
        aria-label={`Help — ${label}`}
        aria-expanded={open}
        data-testid={testId}
        onClick={() => setOpen(v => !v)}
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        <Info className="h-4 w-4" aria-hidden="true" />
      </button>

      {open && (
        <span
          role="dialog"
          aria-label={label}
          // The measured offset rides in as a custom property so it only ever
          // reaches the `lg` rule that reads it. An inline `left` would beat
          // the mobile card's `fixed right-3 bottom-3` at every breakpoint.
          style={{ '--dot-shift': `${shift}px` } as React.CSSProperties}
          className="fixed right-3 bottom-3 z-50 max-h-[60vh] w-[min(20rem,calc(100vw-1.5rem))] overflow-y-auto rounded-md border border-border bg-popover p-3 text-left text-xs leading-relaxed font-normal text-popover-foreground shadow-lg lg:absolute lg:top-6 lg:right-auto lg:bottom-auto lg:left-[var(--dot-shift)] lg:max-h-none lg:w-80 lg:overflow-visible"
        >
          <span className="mb-1 block text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
            {label}
          </span>
          {children}
        </span>
      )}
    </span>
  )
}
